import { useState, useEffect } from "react";
import { useAuth } from "./useAuth";

export const WIDGET_CONFIG_ENDPOINT = "usage/widget-config/";
const SLOT_NUMBERS = [1, 2, 3];
// 404: no config row; 403: usage feature flag off for the team. Both are expected, not errors.
const QUIET_STATUSES = [403, 404];

/**
 * Reduce a widget-config payload (`usage/widget-config/`) to the configured slots, in slot order.
 * A slot with no metric is skipped; an empty array means "nothing configured".
 *
 * Pure — exported so the tab's slot rendering can be tested without a network.
 */
export const selectWidgetSlots = (widgetConfig) => {
  if (!widgetConfig) return [];
  return SLOT_NUMBERS.map((n) => {
    const metricName = widgetConfig[`widget_${n}_metric_name`];
    if (!metricName) return null;
    return {
      slot: n,
      metricName,
      displayName: widgetConfig[`widget_${n}_display_name`] || metricName,
    };
  }).filter(Boolean);
};

/**
 * Team-wide dashboard widget slots for the Product usage tab (PAN-423).
 *
 * Resolves to `slots: []` on 404 or any fetch failure so the tab can fall back to
 * rendering every metric that has data — the pre-PAN-423 behaviour.
 */
export const useUsageWidgetConfig = () => {
  const { apiClient } = useAuth();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchWidgetConfig = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get(WIDGET_CONFIG_ENDPOINT);
        if (!cancelled) setSlots(selectWidgetSlots(response.data));
      } catch (err) {
        if (!QUIET_STATUSES.includes(err.response?.status)) {
          console.error("Error fetching usage widget config:", err);
        }
        if (!cancelled) setSlots([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchWidgetConfig();
    return () => {
      cancelled = true;
    };
  }, [apiClient]);

  return { slots, loading };
};
